<template>
  <div
    :class="componentClass"
    :style="componentStyle"
  >
    <wa-tag
      v-if="onSale"
      theme="yellow"
      label="On Sale!"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="isExpiredPlan && !isInModalContext"
      theme="gray inverted"
      label="Your Expired Plan"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="isCurrentPlan && !isInModalContext"
      theme="blue"
      label="Current Plan"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="plan.mostPopular && !isInModalContext"
      theme="yellow"
      label="Most Popular"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="!plan.proDownloadEnabled && !isInModalContext"
      theme="gravy"
      label="Online Only"
      position="top"
      size="size-sm"
    />

    <h2 class="h1 margin-bottom-2xs">
      <span :style="`color: var(--fa-${planColor});`">
        <font-awesome-icon
          :icon="$v6[planIconStyle][icon]"
          size="lg"
          class="decorative"
        />
      </span>
      <span class="text-nowrap">{{ plan.name }}</span>
    </h2>

    <div style="flex-grow: 1; flex-shrink: 0; flex-basis: auto">
      <template v-if="isInModalContext">
        <h3 class="sr-only">This Plan Includes&hellip;</h3>
        <ul
          class="fa-ul size-sm margin-y-0 display-flex flex-column"
          style="gap: var(--spacing-4xs)"
        >
          <li
            v-if="planHasProPlusIcons"
            class="b"
          >
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v6.solid.faCircleSmall"
                size="xs"
                fixed-width
              />
            </span>
            Includes all Icon Packs
          </li>

          <li :class="featureListItemClass('pro_icon')">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v6.solid.faCircleSmall"
                size="xs"
                fixed-width
              />
            </span>
            Access to all Pro<template v-if="planHasProPlusIcons"> and Pro+</template> Icons
          </li>

          <li :class="featureListItemClass('custom_icon')">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v6.solid.faCircleSmall"
                size="xs"
                fixed-width
              />
            </span>
            <template v-if="plan.customIcons === -1"><font-awesome-icon :icon="$v6.regular.faInfinity" /></template>
            <template v-else>{{ plan.customIcons }}</template>
            Custom icons
          </li>

          <li
            v-if="planHasProDownload"
            :class="featureListItemClass('download')"
          >
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v6.solid.faCircleSmall"
                size="xs"
                fixed-width
              />
            </span>
            Download Pro<template v-if="planHasProPlusIcons"> and Pro+</template> icons
          </li>

          <li :class="featureListItemClass('service')">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v6.solid.faCircleSmall"
                size="xs"
                fixed-width
              />
            </span>
            Extra Services
          </li>
        </ul>

        <p
          v-if="planIsOnlineOnly"
          class="size-sm margin-top-lg"
        >
          Online only via Kits on the web &mdash; No icon download, npm, or Figma
        </p>
      </template>

      <template v-else>
        <h3 class="sr-only">About This Plan</h3>
        <p class="margin-bottom-0">{{ plan.description }}</p>
      </template>
    </div>

    <div>
      <template v-if="isShowingDiscount">
        <div class="row roomy align-middle align-center margin-y-md">
          <div
            v-if="currentDollars !== planPrice"
            class="strong text-center"
            style="color: var(--fa-gravy); --padding-right: var(--spacing-xs); --padding-left: var(--spacing-xs)"
          >
            <span class="sr-only">Regular price:</span>
            <span class="size-4xl tablet:size-3xl laptop:size-4xl"
              ><s>{{ planPrice }}<span class="sr-only">/year</span></s></span
            >
          </div>
          <div
            class="strong"
            style="--padding-right: var(--spacing-xs); --padding-left: var(--spacing-xs)"
          >
            <span class="sr-only">Current price:</span>
            <span class="display-flex align-start align-center">
              <span class="size-4xl tablet:size-3xl laptop:size-4xl">{{ currentDollars }}</span>
              <span
                v-if="currentCents"
                class="size-lg padding-top-2xs"
                >.{{ currentCents }}</span
              >
            </span>
            <span class="sr-only"
              >{{ priceDurationLabel }}<span v-if="discountDuration === 'once'">, then {{ planPrice }}/year</span></span
            >
          </div>
        </div>
        <p
          class="text-center margin-bottom-lg"
          style="color: var(--fa-md-gravy)"
          :aria-label="priceDurationDescription"
          data-balloon-pos="up"
          data-balloon-length="fit"
        >
          <font-awesome-icon
            :icon="$v6.solid.faCalendarDays"
            size="lg"
            class="decorative margin-right-2xs"
          />
          {{ priceDurationLabel }}
        </p>
      </template>

      <div
        v-else
        class="text-center"
      >
        <p class="size-4xl strong margin-y-4xs">
          {{ currentDollars }}<span v-if="currentCents">.{{ currentCents }}</span>
        </p>
        <p
          class="text-center margin-bottom-xl"
          style="color: var(--fa-md-gravy)"
          aria-label="Renews each year"
          data-balloon-pos="up"
        >
          <font-awesome-icon
            :icon="$v6.solid.faCalendarDays"
            size="lg"
            class="decorative margin-right-2xs"
          />
          Billed Annually
        </p>
      </div>

      <span
        v-if="isCurrentPlan && !isExpiredPlan"
        :class="`button flat size-lg block margin-y-0 ${planColor} inverted`"
        style="pointer-events: none"
        >Your Plan!</span
      >

      <span
        v-else-if="isDowngrade"
        class="button flat size-lg block margin-y-0"
        aria-hidden="true"
        style="opacity: 0; pointer-events: none"
      >
        Contact Us
      </span>

      <site-link
        v-else
        :class="`button size-lg block margin-bottom-0 ${planColor}`"
        :to="planLinkArguments(plan)"
        @click.native="trackThen($event, isInModalContext)"
      >
        Get {{ plan.name }}
      </site-link>
    </div>
  </div>
</template>

<script>
  import accounting from 'accounting'
  import { get } from 'lodash'
  import { EventTrackingMixin, ABTestMixin } from 'app/mixins'
  import UtilsMixin from 'app/views/UtilsMixin'
  import { priceWithDiscount, doesDiscountApplyToPlan, formatTotalCurrency, discountRepeatingYears } from 'app/views/plans/priceUtils'

  export default {
    name: 'PlanSummaryCard',

    mixins: [UtilsMixin, EventTrackingMixin, ABTestMixin],

    props: {
      currentSubscription: {
        type: Object
      },
      customStyle: {
        type: Object,
        default: () => {}
      },
      modalContextSlug: {
        type: String
      },
      hasBorder: {
        type: Boolean,
        default: false
      },
      isInModalContext: {
        type: Boolean,
        default: false
      },
      isLast: {
        type: Boolean,
        default: true
      },
      isShowingTag: {
        type: Boolean,
        default: true
      },
      isShowingDescription: {
        type: Boolean,
        default: true
      },
      onSale: {
        type: Boolean,
        default: false
      },
      plan: {
        type: Object
      },
      planColor: {
        type: String
      },
      planIconStyle: {
        type: String,
        default: 'solid'
      },
      subscriptionDiscounts: {
        type: Array,
        required: true
      }
    },

    data() {
      return {
        currentDiscount: null
      }
    },

    computed: {
      buyRoute() {
        return `plans.${this.plan.slug}`
      },

      componentClass() {
        return [
          'plan-card-summary card roomy text-left  display-flex flex-column padding-top-3xl with-top-tag with-border',
          { 'margin-bottom-3xl tablet:margin-bottom-0': !this.isLast && !this.isInModalContext },
          { 'margin-bottom-3xl laptop:margin-bottom-0': !this.isLast && this.isInModalContext },
        ]
      },

      componentStyle() {
        let style = {}

        style['--card-roomy-vertical-padding'] = 'var(--spacing-2xl)'
        style['--card-roomy-horizontal-padding'] = 'var(--spacing-2xl)'
        style['--card-margin-bottom'] = '0'
        style['--border-color'] = 'var(--fa-lt-gravy)'
        style['flex'] = '1'

        if (this.onSale) {
          style['--border-color'] = 'var(--fa-yellow)'
          style['--border-width'] = 'var(--border-width-md)'
        } else if (this.isInModalContext) {
          style['--border-color'] = 'var(--fa-lt-gravy)'
          style['--border-width'] = 'var(--border-width-sm)'
        }

        return {
          ...style,
          ...this.customStyle
        }
      },

      icon() {
        return get(this.plan, ['iconName'], 'faMoon')
      },

      isCurrentPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)

        return this.plan.sku === currentPlanSku
      },

      isDowngrade() {
        return get(this.plan, ['price', 'amount'], 0) < get(this.currentSubscription, ['plan', 'price', 'amount'], 0)
      },

      isExpiredPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        const subStatusCancelled = get(this.$subscription, ['status']) === 'canceled'

        if (this.plan.sku === currentPlanSku && subStatusCancelled) {
          return true
        } else {
          return false
        }
      },

      priceDurationLabel() {
        if (this.discountDuration === 'repeating') {
          return `${discountRepeatingYears(this.currentDiscount)} Year Discount`
        } else if (this.discountDuration === 'once') {
          return 'First Year Discount'
        } else if (this.discountDuration === 'forever') {
          return 'Locked-in Annual Discount'
        } else {
          return 'Billed Annually'
        }
      },

      priceDurationDescription() {
        if (this.discountDuration === 'repeating') {
          return `Enjoy a special deal for ${discountRepeatingYears(this.currentDiscount)} years!`
        } else if (this.discountDuration === 'once') {
          return 'Enjoy a special deal for your first year!'
        } else if (this.discountDuration === 'forever') {
          return "Keep saving as long as you're subscribed!"
        } else {
          return 'Renews each year'
        }
      },

      futurePrice() {
        if (get(this.currentDiscount, 'duration', null) === 'once') {
          return this.planPrice
        } else if (get(this.currentDiscount, 'duration', null) === 'forever') {
          return `${this.currentDollars}.${this.currentCents}`
        } else if (get(this.currentDiscount, 'duration', null) === 'repeating') {
          return `${this.currentDollars}.${this.currentCents}`
        }
        return ''
      },

      currentPrice() {
        let price = get(this.plan, ['price', 'amount'], 0)

        if (this.currentDiscount) {
          const discountedPrice = priceWithDiscount(this.plan, price, this.currentDiscount)

          price = discountedPrice
        }

        return formatTotalCurrency(price / 100)
      },

      isShowingDiscount() {
        return this.subscriptionDiscounts.length > 0
      },

      planHasProPlusIcons() {
        return this.plan?.iconCollection?.id == 3
      },

      planHasProDownload() {
        return this.plan?.proDownloadEnabled
      },

      planIsOnlineOnly() {
        return !this.plan?.proDownloadEnabled
      },

      discountDuration() {
        return get(this.currentDiscount, 'duration', null)
      },

      planPrice() {
        return accounting.formatMoney(get(this.plan, ['price', 'amount'], 0) / 100, null, 0)
      },

      currentDollars() {
        return this.currentPrice[0]
      },

      currentCents() {
        return this.currentPrice[1]
      }
    },

    created() {
      if (this.onSale && this.subscriptionDiscounts.length > 1) {
        this.currentDiscount = this.subscriptionDiscounts.find((discount) => get(discount, ['appliesTo', 'products'], []).includes(this.plan.sku))
      } else if (this.onSale && this.subscriptionDiscounts.length === 1 && doesDiscountApplyToPlan(this.plan, this.subscriptionDiscounts[0])) {
        this.currentDiscount = this.subscriptionDiscounts[0]
      }
    },

    methods: {
      featureListItemClass(feature_slug) {
        return this.modalContextSlug.includes(feature_slug) ? 'b' : null
      },

      trackThen(event, isInModalContext) {
        if (isInModalContext) {
          this.purchaseFlowEvent('get_pro_button_click', {
            context: 'upsell_modal',
            feature: this.modalContextSlug
          })
        }
      },

      planLinkArguments(plan) {
        if (this.currentDiscount) {
          if (doesDiscountApplyToPlan(plan, this.currentDiscount)) {
            return { name: this.buyRoute, query: { discount: this.currentDiscount.id } }
          }
        }

        return { name: this.buyRoute }
      }
    }
  }
</script>
